import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Box,
    Typography,
    CircularProgress,
    Avatar,
    TextField,
    Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { useQueryGetAllUsersList } from '../../hooks/react-query/useData';
import { useMutationCreatePair } from '../../hooks/react-query/pairing';

// Helper functions for user data
const getUserId = (user) => {
    if (!user) return null;
    return user._id || user.id || null;
};

const getUserName = (user) => {
    if (!user) return '';
    if (user.name) return user.name;
    const fname = user.fname || '';
    const lname = user.lname || '';
    return `${fname} ${lname}`.trim();
};

const getUserEmail = (user) => {
    if (!user) return '';
    return user.email || '';
};

const getUserInitials = (user) => {
    if (!user) return '';
    if (user.name) return user.name[0] || '';
    const fname = user.fname || '';
    const lname = user.lname || '';
    return `${fname[0] || ''}${lname[0] || ''}`.trim();
};

// Generic User Autocomplete Component
const UserAutocomplete = ({
    options,
    value,
    onChange,
    label,
    placeholder,
    noOptionsText,
    loading,
}) => {
    return (
        <Autocomplete
            options={options}
            getOptionLabel={(option) => {
                if (!option || typeof option !== 'object') return '';
                return getUserName(option);
            }}
            isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                const optionId = getUserId(option);
                const valueId = getUserId(value);
                if (optionId && valueId) {
                    return String(optionId) === String(valueId);
                }
                return getUserName(option) === getUserName(value) && getUserEmail(option) === getUserEmail(value);
            }}
            filterOptions={(options, { inputValue }) => {
                if (!inputValue || inputValue.trim() === '') {
                    return options;
                }
                const query = inputValue.toLowerCase().trim();
                return options.filter((user) => {
                    const fullName = getUserName(user).toLowerCase();
                    const email = getUserEmail(user).toLowerCase();
                    return fullName.includes(query) || email.includes(query);
                });
            }}
            onChange={onChange}
            value={value}
            loading={loading}
            renderOption={(props, option) => {
                const key = getUserId(option) || `${getUserName(option)}_${getUserEmail(option)}`;
                return (
                    <Box
                        component="li"
                        {...props}
                        key={key}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 1,
                            px: 2,
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        <Avatar
                            src={option.image ? `${option.image}?w=40&h=40&fit=crop&auto=format&q=80` : null}
                            sx={{
                                width: 40,
                                height: 40,
                                bgcolor: 'primary.main',
                                fontSize: '14px',
                                fontWeight: 600,
                                color: 'white',
                            }}
                            alt={getUserName(option)}
                        >
                            {getUserInitials(option)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                {getUserName(option)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {getUserEmail(option)}
                            </Typography>
                        </Box>
                    </Box>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label={label}
                    placeholder={placeholder}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                        },
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            noOptionsText={noOptionsText}
        />
    );
};

export default function CreatePairDialog({
    open,
    handleClose,
    userToken,
    navigate,
    toast,
    queryClient,
}) {
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [selectedEdifier, setSelectedEdifier] = useState(null);
    const [pairingPayload, setPairingPayload] = useState({});

    // Get all users to populate dropdowns
    const { data: allUsers, isLoading: isLoadingUsers } = useQueryGetAllUsersList(
        { token: userToken, role: 'admin' },
        navigate,
        toast
    );


    // Filter users by role and remove duplicates
    const teachers = allUsers?.data?.data?.teachers || [];
    const edifiers = allUsers?.data?.data?.edifiers || [];

    // Pair creation mutation
    const { isLoading: isCreatingPair, mutate: createPair, isSuccess } = useMutationCreatePair(
        userToken,
        navigate,
        toast,
        pairingPayload,
        setPairingPayload,
        queryClient
    );

    // Handle pair creation
    const handleCreatePair = () => {
        if (!selectedTeacher || !selectedEdifier) {
            toast.error('Please select both a teacher and an edifier');
            return;
        }

        const teacherId = selectedTeacher ? getUserId(selectedTeacher) : null;
        const edifierId = selectedEdifier ? getUserId(selectedEdifier) : null;

        if (!teacherId || !edifierId) {
            toast.error('Invalid user selection');
            return;
        }

        if (teacherId === edifierId) {
            toast.error('Teacher and edifier cannot be the same person');
            return;
        }

        setPairingPayload({
            teacherId,
            edifierId,
        });
    };

    // Effect to trigger pair creation when payload is set
    useEffect(() => {
        if (Object.keys(pairingPayload).length > 0 && pairingPayload.teacherId && pairingPayload.edifierId) {
            createPair();
        }
    }, [pairingPayload, createPair]);

    // Handle successful pair creation
    useEffect(() => {
        if (isSuccess) {
            handleCloseDialog();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess]);

    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            setSelectedTeacher(null);
            setSelectedEdifier(null);
            setPairingPayload({});
        }
    }, [open]);

    const handleCloseDialog = () => {
        setSelectedTeacher(null);
        setSelectedEdifier(null);
        setPairingPayload({});
        handleClose();
    };




    return (
        <Dialog
            open={open}
            onClose={handleCloseDialog}
            fullWidth
            maxWidth="sm"
            sx={{
                '& .MuiPaper-root': {
                  height: 'max-content',
                  borderRadius: '20px',
                  maxWidth: { xs: '95%', sm: '500px' },
                  minWidth: { xs: '100%', md: 'fit-content' },
                },
            }}
        >
            <DialogContent sx={{ p: '30px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ color: 'text.titles' }}>
                        Create New Pair
                    </Typography>
                    <CloseIcon
                        onClick={handleCloseDialog}
                        sx={{ cursor: 'pointer', color: 'text.secondary' }}
                    />
                </Box>

                {isLoadingUsers ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <UserAutocomplete
                            options={teachers}
                            value={selectedTeacher}
                            onChange={(event, value) => setSelectedTeacher(value)}
                            label="Select Teacher"
                            placeholder="Search and select a teacher..."
                            noOptionsText="No teachers found. Try a different search term."
                            loading={isLoadingUsers}
                        />

                        <UserAutocomplete
                            options={edifiers}
                            value={selectedEdifier}
                            onChange={(event, value) => setSelectedEdifier(value)}
                            label="Select Edifier"
                            placeholder="Search and select an edifier..."
                            noOptionsText="No edifiers found. Try a different search term."
                            loading={isLoadingUsers}
                        />

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleCloseDialog}
                                disabled={isCreatingPair}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                variant="contained"
                                color="main"
                                onClick={handleCreatePair}
                                loading={isCreatingPair}
                                disabled={!selectedTeacher || !selectedEdifier || isCreatingPair}
                            >
                                Create Pair
                            </LoadingButton>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
